import { Component, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MeoTurn } from '@app/_interfaces/meo.interface';
import { SettingsService } from '@app/_services/settings.service';

// ---------------------------------------------------------------------------
// Typed interfaces for the debug data coming from the backend AiDebugInfo
// ---------------------------------------------------------------------------

interface DebugContextEntity {
  id: string;
  collection: string;
  title: string;
}

interface DebugToolCall {
  round: number;
  name: string;
  args: Record<string, unknown>;
  resultType: 'read' | 'suggestion' | 'answer';
  resultSummary: string;
}

interface ParsedDebugInfo {
  // Timing
  totalDurationMs: number;
  embeddingDurationMs: number;

  // Input
  inputType: string;
  transcript: string | null;
  lang: string;
  userTimezone: string;
  previousTurnsCount: number;

  // Quota
  quotaTier: string;
  quotaUsed: number;
  quotaLimit: number | string;

  // Embedding & Vector Search
  embeddingSuccess: boolean;
  embeddingError?: string;
  vectorSearchResults: number;
  contextEntities: DebugContextEntity[];

  // LLM
  modelUsed: string;
  usedFallback: boolean;
  toolCallRounds: number;
  toolCalls: DebugToolCall[];

  // Response
  responseType: string;
  suggestionsCount: number;
  rawGeminiText?: string;

  // Errors
  errors: string[];
}

interface DebugSection {
  key: string;
  icon: string;
  label: string;
  entries: DebugEntry[];
  colorClass?: string; // 'success' | 'error' | 'warning'
}

interface DebugEntry {
  key: string;
  value: string;
  isComplex?: boolean;
  colorClass?: string; // 'success' | 'error' | 'muted'
}

interface AssistantTurnInfo {
  originalIndex: number;
  turn: MeoTurn;
  turnNumber: number;
  duration: string;
  cost: string;
}

@Component({
  selector: 'app-meo-debug',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './meo-debug.component.html',
  styleUrl: './meo-debug.component.scss',
})
export class MeoDebugComponent {
  readonly ss = inject(SettingsService);
  turns = input<MeoTurn[]>([]);

  /** Track which sections are collapsed. All expanded by default. */
  collapsedSections: Record<string, boolean> = {};

  /** Currently selected turn index (-1 means auto-select latest). */
  selectedTurnIndex = -1;

  toggleSection(key: string): void {
    this.collapsedSections[key] = !this.collapsedSections[key];
  }

  isSectionCollapsed(key: string): boolean {
    return !!this.collapsedSections[key];
  }

  selectTurn(originalIndex: number): void {
    this.selectedTurnIndex = originalIndex;
  }

  /**
   * Returns assistant turns with their metadata for the pill strip.
   */
  getAssistantTurns(): AssistantTurnInfo[] {
    const turns = this.turns();
    const result: AssistantTurnInfo[] = [];
    let num = 1;
    for (let i = 0; i < turns.length; i++) {
      if (turns[i].role === 'assistant') {
        const meta = (turns[i]._debug || turns[i].turnMeta) as Record<string, unknown> | undefined;

        let durationMs = 0;
        let costUsd = 0;
        if (meta) {
          durationMs = typeof meta['totalDurationMs'] === 'number' ? meta['totalDurationMs'] as number
            : typeof meta['durationMs'] === 'number' ? meta['durationMs'] as number : 0;
          if (meta['cost'] && typeof meta['cost'] === 'object') {
            costUsd = (meta['cost'] as Record<string, unknown>)['totalCostUsd'] as number || 0;
          } else if (typeof meta['costUsd'] === 'number') {
            costUsd = meta['costUsd'] as number;
          }
        }

        result.push({
          originalIndex: i,
          turn: turns[i],
          turnNumber: num++,
          duration: this.fmtMs(durationMs),
          cost: this.fmtCost(costUsd),
        });
      }
    }
    return result;
  }

  /**
   * Returns the effective selected turn index.
   * Defaults to the last assistant turn if no explicit selection.
   */
  getEffectiveSelectedIndex(): number {
    const assistantTurns = this.getAssistantTurns();
    if (assistantTurns.length === 0) return -1;

    // If selectedTurnIndex is set and valid, use it
    if (this.selectedTurnIndex >= 0) {
      const exists = assistantTurns.find(at => at.originalIndex === this.selectedTurnIndex);
      if (exists) return this.selectedTurnIndex;
    }

    // Default: last assistant turn
    return assistantTurns[assistantTurns.length - 1].originalIndex;
  }

  isSelectedTurn(originalIndex: number): boolean {
    return this.getEffectiveSelectedIndex() === originalIndex;
  }

  /**
   * Returns the debug info record for the currently selected turn.
   * Prefers full _debug, falls back to turnMeta.
   */
  getSelectedDebugInfo(): Record<string, unknown> | null {
    const idx = this.getEffectiveSelectedIndex();
    if (idx < 0) return null;
    const turn = this.turns()[idx];
    if (!turn) return null;
    return turn._debug as Record<string, unknown> || turn.turnMeta as Record<string, unknown> || null;
  }

  /**
   * Returns true when the selected turn has only turnMeta (no full _debug).
   */
  hasLimitedDebug(): boolean {
    const idx = this.getEffectiveSelectedIndex();
    if (idx < 0) return false;
    const turn = this.turns()[idx];
    return turn?.turnMeta !== undefined && turn?._debug === undefined;
  }

  /**
   * Computes thread-level summary across all assistant turns.
   */
  getThreadSummary(): { totalCost: string; turnCount: number; totalDuration: string } | null {
    const assistantTurns = this.getAssistantTurns();
    if (assistantTurns.length === 0) return null;

    let totalCostUsd = 0;
    let totalDurationMs = 0;

    for (const at of assistantTurns) {
      const meta = (at.turn._debug || at.turn.turnMeta) as Record<string, unknown> | undefined;
      if (meta) {
        // Cost: from _debug (nested) or turnMeta (flat)
        if (meta['cost'] && typeof meta['cost'] === 'object') {
          totalCostUsd += (meta['cost'] as Record<string, unknown>)['totalCostUsd'] as number || 0;
        } else if (typeof meta['costUsd'] === 'number') {
          totalCostUsd += meta['costUsd'] as number;
        }

        // Duration: from _debug or turnMeta
        if (typeof meta['totalDurationMs'] === 'number') {
          totalDurationMs += meta['totalDurationMs'] as number;
        } else if (typeof meta['durationMs'] === 'number') {
          totalDurationMs += meta['durationMs'] as number;
        }
      }
    }

    return {
      totalCost: this.fmtCost(totalCostUsd),
      turnCount: assistantTurns.length,
      totalDuration: this.fmtMs(totalDurationMs),
    };
  }

  /**
   * Parse the raw debug record into typed, display-ready sections.
   */
  getSections(): DebugSection[] {
    const raw = this.getSelectedDebugInfo();
    if (!raw) return [];

    const sections: DebugSection[] = [];

    // --- TIMING ---
    const info = this.parseDebugInfo(raw);
    const estimatedLlmMs = Math.max(0, info.totalDurationMs - info.embeddingDurationMs);
    sections.push({
      key: 'timing',
      icon: '\u23F1',
      label: 'TIMING',
      entries: [
        { key: 'Total', value: this.fmtMs(info.totalDurationMs) },
        { key: 'Embedding', value: this.fmtMs(info.embeddingDurationMs) },
        { key: 'LLM (est.)', value: `~${this.fmtMs(estimatedLlmMs)}` },
      ],
    });

    // --- COST & TOKENS ---
    const tokens = raw['tokens'] as { totalInput: number; totalOutput: number; total: number; breakdown?: Array<{ step: string; inputTokens: number; outputTokens: number }> } | undefined;
    const cost = raw['cost'] as { inputTokensCost: number; outputTokensCost: number; totalCostUsd: number } | undefined;

    // Full _debug has tokens/cost as nested objects
    if (tokens || cost) {
      const costEntries: DebugEntry[] = [];
      if (cost) {
        costEntries.push({ key: 'Total cost', value: this.fmtCost(cost.totalCostUsd) });
        costEntries.push({ key: 'Input cost', value: this.fmtCost(cost.inputTokensCost) });
        costEntries.push({ key: 'Output cost', value: this.fmtCost(cost.outputTokensCost) });
      }
      if (tokens) {
        costEntries.push({ key: 'Input tokens', value: this.fmtNum(tokens.totalInput) });
        costEntries.push({ key: 'Output tokens', value: this.fmtNum(tokens.totalOutput) });
        costEntries.push({ key: 'Total tokens', value: this.fmtNum(tokens.total) });
        if (tokens.breakdown && tokens.breakdown.length > 0) {
          for (const b of tokens.breakdown) {
            costEntries.push({
              key: `  [${b.step}]`,
              value: `in: ${this.fmtNum(b.inputTokens)}  out: ${this.fmtNum(b.outputTokens)}`,
              colorClass: 'muted',
            });
          }
        }
      }
      sections.push({ key: 'cost', icon: '\uD83D\uDCB0', label: 'COST & TOKENS', entries: costEntries });
    }
    // Fallback: turnMeta only (after page refresh) — flat fields
    else if (raw['costUsd'] !== undefined || raw['tokensIn'] !== undefined) {
      const fallbackEntries: DebugEntry[] = [];
      if (raw['costUsd'] !== undefined) {
        fallbackEntries.push({ key: 'Total cost', value: this.fmtCost(this.num(raw['costUsd'])) });
      }
      if (raw['tokensIn'] !== undefined) {
        fallbackEntries.push({ key: 'Input tokens', value: this.fmtNum(this.num(raw['tokensIn'])) });
      }
      if (raw['tokensOut'] !== undefined) {
        fallbackEntries.push({ key: 'Output tokens', value: this.fmtNum(this.num(raw['tokensOut'])) });
      }
      sections.push({ key: 'cost', icon: '\uD83D\uDCB0', label: 'COST & TOKENS', entries: fallbackEntries });
    }

    // --- INPUT ---
    const inputEntries: DebugEntry[] = [
      { key: 'Type', value: info.inputType },
    ];
    if (info.transcript) {
      inputEntries.push({ key: 'Transcript', value: `"${info.transcript}"` });
    }
    if (info.lang) {
      inputEntries.push({ key: 'Language', value: info.lang });
    }
    if (info.userTimezone) {
      inputEntries.push({ key: 'Timezone', value: info.userTimezone });
    }
    if (info.previousTurnsCount > 0) {
      inputEntries.push({ key: 'Previous turns', value: String(info.previousTurnsCount) });
    }
    sections.push({
      key: 'input',
      icon: '\uD83D\uDCE5',
      label: 'INPUT',
      entries: inputEntries,
    });

    // --- CONTEXT ---
    const ctx = raw['context'] as Record<string, unknown> | undefined;
    const contextEntries: DebugEntry[] = [];
    if (ctx) {
      // Full _debug has rich context info
      contextEntries.push(
        { key: 'System prompt', value: `${this.fmtNum(this.num(ctx['systemPromptLength']))} chars (~${this.fmtNum(this.num(ctx['systemPromptEstTokens']))} tokens)` },
        { key: 'Context entities', value: `${this.num(ctx['contextEntitiesCount'])} entities (${this.fmtNum(this.num(ctx['contextEntitiesChars']))} chars)` },
        { key: 'Previous turns', value: String(this.num(ctx['previousTurnsCount'])) },
        { key: 'History messages', value: String(this.num(ctx['historyMessagesCount'])) },
      );
    } else {
      // Fallback: flat fields
      contextEntries.push(
        { key: 'Context entities', value: `${info.contextEntities.length} entities` },
        { key: 'Previous turns', value: String(info.previousTurnsCount) },
      );
    }
    if (contextEntries.length > 0) {
      sections.push({
        key: 'context',
        icon: '\uD83D\uDCCB',
        label: 'CONTEXT',
        entries: contextEntries,
      });
    }

    // --- VECTOR SEARCH ---
    // Read from nested vectorSearch object if available (_debug), otherwise use flat fields
    const vs = raw['vectorSearch'] as Record<string, unknown> | undefined;
    const vsEntries: DebugEntry[] = [];
    if (vs) {
      // Full _debug has rich vector search info
      if (vs['queryText']) {
        vsEntries.push({ key: 'Query', value: `"${vs['queryText']}"` });
      }
      vsEntries.push(
        { key: 'Dimension', value: String(this.num(vs['embeddingDimension']) || 256) },
        { key: 'Top-K', value: String(this.num(vs['topK']) || 10) },
        { key: 'Distance', value: this.str(vs['distanceMeasure'], 'COSINE') },
        { key: 'Index status', value: this.str(vs['indexStatus'], 'unknown') },
        {
          key: 'Embedding',
          value: info.embeddingSuccess ? 'success' : `failed${info.embeddingError ? ': ' + info.embeddingError : ''}`,
          colorClass: info.embeddingSuccess ? 'success' : 'error',
        },
        { key: 'Results', value: String(this.num(vs['resultsCount'])) },
      );
      // Show matched entities from vector search results
      const vsResults = Array.isArray(vs['results']) ? vs['results'] as Array<Record<string, unknown>> : [];
      for (const r of vsResults) {
        const truncatedId = String(r['entityId'] || '').length > 8 ? String(r['entityId']).substring(0, 8) + '...' : String(r['entityId'] || '');
        vsEntries.push({
          key: `  [${r['collection']}]`,
          value: `${r['title']} (id: ${truncatedId})`,
          colorClass: 'muted',
        });
      }
    } else if (info.vectorSearchResults > 0 || info.embeddingSuccess || info.contextEntities.length > 0) {
      // Fallback: flat fields from _debug or turnMeta
      vsEntries.push(
        {
          key: 'Embedding',
          value: info.embeddingSuccess ? 'success' : 'failed',
          colorClass: info.embeddingSuccess ? 'success' : 'error',
        },
        { key: 'Results', value: String(info.vectorSearchResults) },
      );
      for (const entity of info.contextEntities) {
        const truncatedId = entity.id.length > 8 ? entity.id.substring(0, 8) + '...' : entity.id;
        vsEntries.push({
          key: `  [${entity.collection}]`,
          value: `${entity.title} (id: ${truncatedId})`,
          colorClass: 'muted',
        });
      }
    }
    if (vsEntries.length > 0) {
      sections.push({
        key: 'vectorSearch',
        icon: '\uD83D\uDD0D',
        label: 'VECTOR SEARCH',
        entries: vsEntries,
      });
    }

    // --- TOOL CALLS ---
    const toolEntries: DebugEntry[] = [];
    // turnMeta has toolNames (string[]) instead of toolCalls (object[])
    const toolNames = Array.isArray(raw['toolNames']) ? raw['toolNames'] as string[] : [];
    if (info.toolCalls.length === 0 && toolNames.length === 0) {
      toolEntries.push({ key: 'Calls', value: 'none', colorClass: 'muted' });
    } else if (info.toolCalls.length === 0 && toolNames.length > 0) {
      // Limited mode: only tool names available from turnMeta
      for (const name of toolNames) {
        toolEntries.push({ key: name, value: '', colorClass: 'muted' });
      }
    } else {
      for (const tc of info.toolCalls) {
        toolEntries.push({
          key: `[Round ${tc.round}] ${tc.name}`,
          value: '',
        });
        // Show entity data for create/update tools
        const isEntityTool = tc.name === 'createEntity' || tc.name === 'updateEntity';
        if (isEntityTool) {
          if (tc.args['collection']) {
            toolEntries.push({ key: '  Collection', value: String(tc.args['collection']), colorClass: 'muted' });
          }
          if (tc.args['title']) {
            toolEntries.push({ key: '  Title', value: String(tc.args['title']), colorClass: 'muted' });
          }
          if (tc.args['entityId']) {
            toolEntries.push({ key: '  Entity ID', value: String(tc.args['entityId']), colorClass: 'muted' });
          }
          // Show the full entity fields as formatted JSON
          const entityData = tc.args['fields'] || tc.args['fieldsToUpdate'] || tc.args['entity'];
          if (entityData && typeof entityData === 'object') {
            toolEntries.push({
              key: '  Entity data',
              value: JSON.stringify(entityData, null, 2),
              isComplex: true,
            });
          }
        } else {
          // For other tools, show compact args summary
          const argKeys = Object.keys(tc.args);
          if (argKeys.length > 0) {
            const argSummary = argKeys.map(k => {
              const v = tc.args[k];
              const valStr = typeof v === 'string' ? v : JSON.stringify(v);
              const truncated = valStr.length > 60 ? valStr.substring(0, 60) + '...' : valStr;
              return `${k}: ${truncated}`;
            }).join(', ');
            toolEntries.push({ key: '  Args', value: argSummary, colorClass: 'muted' });
          }
        }
        toolEntries.push({
          key: '  Result',
          value: tc.resultSummary,
          colorClass: tc.resultType === 'suggestion' ? 'success' : undefined,
        });
      }
    }
    sections.push({
      key: 'toolCalls',
      icon: '\uD83D\uDD27',
      label: `TOOL CALLS (${info.toolCalls.length || toolNames.length} calls)`,
      entries: toolEntries,
    });

    // --- LLM ---
    const llmEntries: DebugEntry[] = [
      { key: 'Model', value: info.modelUsed },
      {
        key: 'Fallback used',
        value: info.usedFallback ? 'Yes' : 'No',
        colorClass: info.usedFallback ? 'warning' : 'success',
      },
    ];
    if (info.rawGeminiText !== undefined) {
      const displayText = info.rawGeminiText
        ? (info.rawGeminiText.length > 200 ? info.rawGeminiText.substring(0, 200) + '...' : info.rawGeminiText)
        : 'null (tool call response)';
      llmEntries.push({ key: 'Raw text', value: displayText });
    }
    sections.push({
      key: 'llm',
      icon: '\uD83E\uDD16',
      label: 'LLM',
      entries: llmEntries,
    });

    // --- QUOTA --- (skip if no data — turnMeta doesn't have quota info)
    if (info.quotaTier || info.quotaUsed > 0) {
      sections.push({
        key: 'quota',
        icon: '\uD83D\uDCCA',
        label: 'QUOTA',
        entries: [
          { key: 'Tier', value: info.quotaTier },
          { key: 'Used', value: String(info.quotaUsed) },
          { key: 'Limit', value: String(info.quotaLimit) },
        ],
      });
    }

    // --- ERRORS ---
    const errEntries: DebugEntry[] = info.errors.length > 0
      ? info.errors.map((e, i) => ({ key: `[${i}]`, value: e, colorClass: 'error' as const }))
      : [{ key: '', value: '(none)', colorClass: 'success' as const }];
    sections.push({
      key: 'errors',
      icon: '\u26A0\uFE0F',
      label: 'ERRORS',
      entries: errEntries,
      colorClass: info.errors.length > 0 ? 'error' : undefined,
    });

    return sections;
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private parseDebugInfo(raw: Record<string, unknown>): ParsedDebugInfo {
    return {
      // _debug uses totalDurationMs, turnMeta uses durationMs
      totalDurationMs: this.num(raw['totalDurationMs']) || this.num(raw['durationMs']),
      // _debug uses embeddingDurationMs, turnMeta uses embeddingMs
      embeddingDurationMs: this.num(raw['embeddingDurationMs']) || this.num(raw['embeddingMs']),
      inputType: this.str(raw['inputType'], 'text'),
      transcript: raw['transcript'] as string | null ?? null,
      lang: this.str(raw['lang'], ''),
      userTimezone: this.str(raw['userTimezone'], ''),
      previousTurnsCount: this.num(raw['previousTurnsCount']),
      quotaTier: this.str(raw['quotaTier'], ''),
      quotaUsed: this.num(raw['quotaUsed']),
      quotaLimit: raw['quotaLimit'] as number | string ?? 0,
      // _debug uses embeddingSuccess, turnMeta uses embeddingOk
      embeddingSuccess: raw['embeddingSuccess'] === true || raw['embeddingOk'] === true,
      embeddingError: raw['embeddingError'] as string | undefined,
      // _debug uses vectorSearchResults, turnMeta uses vectorHits
      vectorSearchResults: this.num(raw['vectorSearchResults']) || this.num(raw['vectorHits']),
      contextEntities: Array.isArray(raw['contextEntities'])
        ? (raw['contextEntities'] as DebugContextEntity[])
        : [],
      // _debug uses modelUsed, turnMeta uses modelId
      modelUsed: this.str(raw['modelUsed'], '') || this.str(raw['modelId'], ''),
      usedFallback: raw['usedFallback'] === true,
      toolCallRounds: this.num(raw['toolCallRounds']),
      toolCalls: Array.isArray(raw['toolCalls'])
        ? (raw['toolCalls'] as DebugToolCall[])
        : [],
      responseType: this.str(raw['responseType'], ''),
      suggestionsCount: this.num(raw['suggestionsCount']),
      rawGeminiText: raw['rawGeminiText'] as string | undefined,
      errors: Array.isArray(raw['errors'])
        ? (raw['errors'] as string[])
        : [],
    };
  }

  private num(v: unknown): number {
    return typeof v === 'number' ? v : 0;
  }

  private str(v: unknown, fallback: string): string {
    return typeof v === 'string' ? v : fallback;
  }

  fmtMs(ms: number): string {
    if (ms >= 1000) {
      return `${(ms / 1000).toFixed(1)}s`;
    }
    return `${ms}ms`;
  }

  fmtCost(usd: number): string {
    if (usd === 0) return '$0';
    if (usd < 0.0001) return `$${usd.toFixed(6)}`;
    if (usd < 0.01) return `$${usd.toFixed(4)}`;
    return `$${usd.toFixed(4)}`;
  }

  fmtNum(n: number): string {
    return n.toLocaleString();
  }
}
